"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { audioBus } from "@/lib/audio-bus";
import {
  Music, Plus, Pencil, Trash2, Search, Globe, Star, Volume2,
  Play, Pause, X, Check, Headphones, Tag, Sparkles, Filter,
  ChevronDown, Loader2, Disc3,
} from "lucide-react";
import { ImageUploader } from "@/components/admin/uploads/ImageUploader";
import { AudioUploader } from "@/components/admin/uploads/AudioUploader";
import { Dialog, ConfirmDialog } from "@/components/ui/Dialog";
import { cn } from "@/lib/utils";
import { adminApi } from "@/lib/adminApi";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface AudioTrack {
  id: string;
  title: string;
  artist: string | null;
  url: string;
  duration: number | null;
  provider: string;
  type: string;
  mood: string | null;
  genre: string | null;
  proposalType: string | null;
  language: string;
  isGeneric: boolean;
  isActive: boolean;
  isFeatured: boolean;
  tier: "FREE" | "PRO";
  coverArtUrl: string | null;
  createdAt: string;
}

interface MusicTag {
  id: string;
  kind: "theme" | "mood";
  slug: string;
  name: string;
  icon: string | null;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
}

interface Stats {
  total: number;
  active: number;
  featured: number;
  byMood: { mood: string; count: number }[];
  byGenre: { genre: string; count: number }[];
}

/* ─── Constants ──────────────────────────────────────────────────────── */

const LANGUAGES = [
  { value: "en", label: "English", flag: "🇺🇸" },
  { value: "fr", label: "French", flag: "🇫🇷" },
  { value: "es", label: "Spanish", flag: "🇪🇸" },
  { value: "ko", label: "Korean", flag: "🇰🇷" },
  { value: "de", label: "German", flag: "🇩🇪" },
  { value: "pt", label: "Portuguese", flag: "🇵🇹" },
  { value: "multi", label: "Multi-language", flag: "🌐" },
];
const GENRES = ["ballad", "pop", "acoustic", "orchestral", "rnb", "classical", "jazz", "electronic", "ambient"];
const PROPOSAL_TYPES = ["MARRIAGE", "RELATIONSHIP", "DATE"];
const TYPES = [
  { value: "original", label: "Original", icon: "✨" },
  { value: "remix", label: "Remix", icon: "🔁" },
  { value: "cover", label: "Cover", icon: "🎤" },
];

const TRACK_FORM_DEFAULTS = {
  title: "", artist: "", url: "", duration: "",
  audioDurationSec: null as number | null,
  coverArtUrl: "",
  type: "original", mood: "", genre: "", proposalType: "",
  language: "en", isGeneric: false, isActive: true, isFeatured: false, tier: "FREE",
};

/* ─── Page ───────────────────────────────────────────────────────────── */

type Tab = "tracks" | "tags";

export default function AudioPage() {
  const [tab, setTab] = useState<Tab>("tracks");

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-pink-400">
            Library
          </p>
          <h1 className="mt-1 text-2xl font-bold text-white sm:text-3xl">Platform Audio</h1>
          <p className="mt-1 text-sm text-white/60">
            Curate the fallback music library and the theme/mood options shown on the
            create-music wizard.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="inline-flex rounded-2xl border border-white/10 bg-white/5 p-1 shadow-inner">
          {([
            { key: "tracks", label: "Tracks", icon: Headphones },
            { key: "tags",   label: "Music Tags", icon: Tag },
          ] as const).map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={cn(
                  "relative inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition",
                  active ? "text-white" : "text-white/60 hover:text-white",
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            );
          })}
        </div>
      </header>

      {tab === "tracks" ? <TracksTab /> : <TagsTab />}
    </div>
  );
}

/* ─── Tracks tab ─────────────────────────────────────────────────────── */

function TracksTab() {
  const [tracks, setTracks] = useState<AudioTrack[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterLanguage, setFilterLanguage] = useState<string>("");
  const [filterTier, setFilterTier] = useState<string>("");
  const [filterActive, setFilterActive] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Canonical theme + mood names from the MusicTag table. The Mood
  // datalist in the create form reads from this list so the admin
  // can curate new moods in /admin/audio > Music Tags and have them
  // appear automatically. Empty state when the fetch hasn't returned.
  const [musicTags, setMusicTags] = useState<Array<{ name: string; kind: "theme" | "mood" }>>([]);
  const [total, setTotal] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editingTrack, setEditingTrack] = useState<AudioTrack | null>(null);
  const [form, setForm] = useState({ ...TRACK_FORM_DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const fetchTracks = async (pageNum = 1) => {
    setLoading(true);
    try {
      // The list endpoint supports language / tier / featuredOnly as
      // server-side filters, but we only need the language filter to be
      // server-side when the page count matters. For a snappy admin UI
      // we fetch the whole list once and let the client-side
      // `filteredTracks` memo combine all filters (search / language /
      // tier / status) without round-trips.
      const params = new URLSearchParams({
        page: "1",
        limit: "200",
        activeOnly: "false",
      });
      const res = await adminApi.get<{
        success: boolean;
        tracks: AudioTrack[];
        total: number;
        pages: number;
      }>(`/admin/audio?${params}`);
      if (res.success) {
        setTracks(res.tracks);
        setTotal(res.total);
        setTotalPages(res.pages);
        setPage(1);
      }
    } catch (e: any) {
      setError(e.message || "Failed to load audio tracks");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await adminApi.get<{ success: boolean; stats: Stats }>("/admin/audio/stats/summary");
      if (res.success) setStats(res.stats);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    }
  };

  useEffect(() => {
    fetchTracks(1);
    fetchStats();
    // Fetch canonical theme/mood tag values for the create form's
    // Mood datalist. The TagsTab does its own admin-scoped fetch; this
    // is a parallel public read for the create form's autocomplete.
    adminApi
      .get<{ success: boolean; tags: { name: string; kind: "theme" | "mood" }[] }>(
        "/admin/music-tags",
      )
      .then((r) => {
        if (r.success) setMusicTags(r.tags);
      })
      .catch(() => {/* leave empty — datalist falls back gracefully */});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingTrack(null);
    setForm({ ...TRACK_FORM_DEFAULTS });
    setError(null);
    setShowModal(true);
  };

  const openEdit = (track: AudioTrack) => {
    setEditingTrack(track);
    setForm({
      title: track.title,
      artist: track.artist || "",
      url: track.url,
      duration: track.duration?.toString() || "",
      audioDurationSec: track.duration ?? null,
      coverArtUrl: track.coverArtUrl || "",
      type: track.type,
      mood: track.mood || "",
      genre: track.genre || "",
      proposalType: track.proposalType || "",
      language: track.language,
      isGeneric: track.isGeneric,
      isActive: track.isActive,
      isFeatured: track.isFeatured,
      tier: track.tier,
    });
    setError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.url.trim()) {
      setError("Title and URL are required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: form.title.trim(),
        artist: form.artist.trim() || null,
        url: form.url.trim(),
        duration: form.audioDurationSec != null
          ? Math.round(form.audioDurationSec)
          : (form.duration ? parseInt(form.duration) : null),
        coverArtUrl: form.coverArtUrl.trim() || null,
        type: form.type,
        mood: form.mood || null,
        genre: form.genre || null,
        proposalType: form.proposalType || null,
        language: form.language,
        isGeneric: form.isGeneric,
        isActive: form.isActive,
        isFeatured: form.isFeatured,
        tier: form.tier,
      };
      if (editingTrack) {
        await adminApi.put(`/admin/audio/${editingTrack.id}`, payload);
      } else {
        await adminApi.post("/admin/audio", payload);
      }
      setShowModal(false);
      fetchTracks(page);
      fetchStats();
    } catch (e: any) {
      setError(e.message || "Failed to save track");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setConfirmDeleteId(null);
    try {
      await adminApi.delete(`/admin/audio/${id}`);
      fetchTracks(page);
      fetchStats();
    } catch (e: any) {
      setError(e.message || "Failed to delete track");
    }
  };

  // Client-side filter pass — combines search + language + tier + status.
  // All filters compose (AND) and run on the full list fetched once,
  // so changing any filter chip is instant with no server round-trip.
  const filteredTracks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tracks.filter((t) => {
      // Free-text search across title + artist.
      if (q) {
        const hay = `${t.title} ${t.artist ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterLanguage && t.language !== filterLanguage) return false;
      if (filterTier && t.tier !== filterTier) return false;
      if (filterActive === "active" && !t.isActive) return false;
      if (filterActive === "inactive" && t.isActive) return false;
      if (filterActive === "featured" && !t.isFeatured) return false;
      return true;
    });
  }, [tracks, search, filterLanguage, filterTier, filterActive]);

  return (
    <>
      {/* Stats row */}
      {stats && <StatsRow stats={stats} />}

      {/* Filter bar + Add */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative min-w-[200px] flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              placeholder="Search by mood…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); fetchTracks(1); }}
              className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-2 pl-10 pr-4 text-sm text-white placeholder-white/30 outline-none transition focus:border-pink-500/50 focus:bg-white/[0.07]"
            />
          </div>

          {/* Language dropdown — replaced with chip-style selector */}
          <ChipDropdown
            icon={Globe}
            label="Language"
            value={filterLanguage}
            options={[
              { value: "", label: "All languages" },
              ...LANGUAGES.map((l) => ({ value: l.value, label: `${l.flag} ${l.label}` })),
            ]}
            onChange={(v) => { setFilterLanguage(v); }}
          />

          {/* Tier chips */}
          <ChipGroup
            label="Tier"
            value={filterTier}
            options={[
              { value: "", label: "All" },
              { value: "FREE", label: "FREE" },
              { value: "PRO", label: "PRO" },
            ]}
            onChange={setFilterTier}
          />

          {/* Status chips */}
          <ChipGroup
            label="Status"
            value={filterActive}
            options={[
              { value: "", label: "Any" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
              { value: "featured", label: "Featured" },
            ]}
            onChange={setFilterActive}
          />
        </div>

        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 self-start rounded-xl bg-merry-pink-gradient px-4 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_-6px_rgba(255,51,119,0.55)] transition hover:scale-[1.02] active:scale-[0.99] lg:self-auto"
        >
          <Plus className="h-4 w-4" />
          Add Track
        </button>
      </div>

      {/* Track grid */}
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-2 sm:p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-pink-400" />
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <p className="text-sm text-red-300">{error}</p>
            <button onClick={() => fetchTracks(page)} className="mt-2 text-xs text-pink-400 hover:underline">
              Retry
            </button>
          </div>
        ) : filteredTracks.length === 0 ? (
          <div className="py-16 text-center">
            <Disc3 className="mx-auto h-12 w-12 text-white/15" />
            <p className="mt-4 text-sm text-white/60">No audio tracks match these filters</p>
            <button onClick={openCreate} className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-pink-300 hover:text-pink-200">
              <Plus className="h-3.5 w-3.5" />
              Add the first track
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3">
            {filteredTracks.map((track) => (
              <TrackCard
                key={track.id}
                track={track}
                onEdit={() => openEdit(track)}
                onDelete={() => setConfirmDeleteId(track.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && total > 0 && (
        <div className="flex items-center justify-between text-sm text-white/60">
          <p>
            Page {page} of {totalPages} · {total} tracks
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fetchTracks(page - 1)}
              disabled={page <= 1}
              className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => fetchTracks(page + 1)}
              disabled={page >= totalPages}
              className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        title={
          <span className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-pink-400" />
            {editingTrack ? "Edit Audio Track" : "Add Audio Track"}
          </span>
        }
        width={720}
      >
        {error && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-2">
          {/* Identity */}
          <Section title="Identity">
            <Field label="Title *" htmlFor="audio-title">
              <input
                id="audio-title"
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Our Song"
                className={inputCls}
              />
            </Field>
            <Field label="Artist" htmlFor="audio-artist">
              <input
                id="audio-artist"
                type="text"
                value={form.artist}
                onChange={(e) => setForm((f) => ({ ...f, artist: e.target.value }))}
                placeholder="MerryFlix AI"
                className={inputCls}
              />
            </Field>
          </Section>

          {/* Cover art + Audio file */}
          <Section title="Media">
            <Field label="Cover art" htmlFor="audio-cover">
              <ImageUploader
                value={form.coverArtUrl}
                onChange={(url) => setForm((f) => ({ ...f, coverArtUrl: url }))}
              />
            </Field>
            <Field label="Audio file *" htmlFor="audio-file">
              <AudioUploader
                value={form.url}
                durationHintSeconds={form.audioDurationSec}
                onChange={(url, durationSec) =>
                  setForm((f) => ({
                    ...f,
                    url,
                    audioDurationSec: durationSec,
                    duration:
                      durationSec && isFinite(durationSec)
                        ? Math.round(durationSec).toString()
                        : f.duration,
                  }))
                }
              />
            </Field>
          </Section>

          {/* Categorisation */}
          <Section title="Categorisation" cols={2}>
            <Field label="Language" htmlFor="audio-language">
              <SelectChips
                value={form.language}
                onChange={(v) => setForm((f) => ({ ...f, language: v }))}
                options={LANGUAGES.map((l) => ({ value: l.value, label: `${l.flag} ${l.label}` }))}
              />
            </Field>
            <Field label="Type" htmlFor="audio-type">
              <SelectChips
                value={form.type}
                onChange={(v) => setForm((f) => ({ ...f, type: v }))}
                options={TYPES.map((t) => ({ value: t.value, label: `${t.icon} ${t.label}` }))}
              />
            </Field>
            <Field label="Mood" htmlFor="audio-mood">
              <input
                id="audio-mood"
                type="text"
                value={form.mood}
                onChange={(e) => setForm((f) => ({ ...f, mood: e.target.value }))}
                placeholder="e.g. romantic"
                list="audio-moods"
                className={inputCls}
              />
              <datalist id="audio-moods">
                {musicTags
                  .filter((t) => t.kind === "mood")
                  .map((m) => (
                    <option key={m.name} value={m.name} />
                  ))}
              </datalist>
            </Field>
            <Field label="Genre" htmlFor="audio-genre">
              <input
                id="audio-genre"
                type="text"
                value={form.genre}
                onChange={(e) => setForm((f) => ({ ...f, genre: e.target.value }))}
                placeholder="e.g. pop"
                list="audio-genres"
                className={inputCls}
              />
              <datalist id="audio-genres">
                {GENRES.map((g) => <option key={g} value={g} />)}
              </datalist>
            </Field>
            <Field label="Proposal Type" htmlFor="audio-proptype">
              <SelectChips
                value={form.proposalType}
                onChange={(v) => setForm((f) => ({ ...f, proposalType: v }))}
                options={[
                  { value: "", label: "All" },
                  ...PROPOSAL_TYPES.map((p) => ({ value: p, label: p.charAt(0) + p.slice(1).toLowerCase() })),
                ]}
              />
            </Field>
            <Field label="Tier" htmlFor="audio-tier">
              <SelectChips
                value={form.tier}
                onChange={(v) => setForm((f) => ({ ...f, tier: v as "FREE" | "PRO" }))}
                options={[
                  { value: "FREE", label: "FREE" },
                  { value: "PRO",  label: "PRO" },
                ]}
              />
            </Field>
          </Section>

          {/* Flags */}
          <Section title="Flags">
            <ToggleRow
              label="Is Active"
              description="Tracks marked inactive are hidden from the picker."
              checked={form.isActive}
              onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
            />
            <ToggleRow
              label="Is Featured"
              description="Featured tracks show first in the wizard."
              checked={form.isFeatured}
              onChange={(v) => setForm((f) => ({ ...f, isFeatured: v }))}
            />
            <ToggleRow
              label="Is Generic (instrumental)"
              description="Generic tracks are usable across all languages."
              checked={form.isGeneric}
              onChange={(v) => setForm((f) => ({ ...f, isGeneric: v }))}
            />
          </Section>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3 border-t border-white/10 pt-4">
          <button
            type="button"
            onClick={() => setShowModal(false)}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-merry-pink-gradient px-5 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-6px_rgba(255,51,119,0.55)] transition hover:scale-[1.02] active:scale-[0.99] disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {editingTrack ? "Save changes" : "Create track"}
          </button>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => confirmDeleteId && handleDelete(confirmDeleteId)}
        title="Delete this track?"
        description="This permanently removes the audio track from the platform library. It cannot be undone."
        confirmLabel="Delete track"
        variant="destructive"
      />
    </>
  );
}

/* ─── Track card with mini player ──────────────────────────────────────
 * UI/UX adapted from the web frontend's `MusicTrackTile`
 * (merryflix-frontend/src/components/ui2/MusicTrackTile.tsx) so the
 * admin list view matches what the user sees on the wizard's step 4.
 *
 * Differences from the web card:
 *  - admin list view: square card grid, so we keep the rounded card +
 *    action row at the bottom
 *  - admin has "Edit" / "Delete" actions on the card
 *  - no real wavesurfer renderer in admin (simpler <audio> + mini
 *    equalizer + progress bar is enough for previewing)
 */
const TRACK_PALETTES = [
  "from-pink-500 via-rose-500 to-fuchsia-600",
  "from-amber-500 via-orange-500 to-rose-500",
  "from-cyan-500 via-blue-500 to-indigo-600",
  "from-emerald-500 via-teal-500 to-cyan-600",
  "from-purple-500 via-pink-500 to-rose-500",
  "from-yellow-500 via-orange-500 to-red-500",
  "from-violet-500 via-fuchsia-500 to-pink-500",
];

const PROPOSAL_TYPE_META_ADMIN: Record<string, { emoji: string; label: string; classes: string }> = {
  MARRIAGE:    { emoji: "💍", label: "Marriage",    classes: "bg-pink-500/80" },
  RELATIONSHIP: { emoji: "❤️", label: "Relationship", classes: "bg-rose-500/80" },
  DATE:        { emoji: "🍸", label: "Date",        classes: "bg-purple-500/80" },
};

function TrackCard({ track, onEdit, onDelete }: {
  track: AudioTrack;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveContainerRef = useRef<HTMLDivElement | null>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const currentTimeRef = useRef(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      if (a.duration > 0) {
        setProgress(a.currentTime / a.duration);
        currentTimeRef.current = a.currentTime;
      }
    };
    const onEnd = () => {
      setIsPlaying(false);
      setProgress(0);
      currentTimeRef.current = 0;
    };
    const onWaiting = () => setIsLoading(true);
    const onPlaying = () => { setIsLoading(false); setIsPlaying(true); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("playing", onPlaying);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("playing", onPlaying);
    };
  }, [track.url]);

  // Build the wavesurfer instance + a hidden <audio> element so the
  // browser handles streaming + seeking and we don't pay the cost of
  // a second network fetch. The wavesurfer is the progress bar AND
  // the visual waveform — matches the web frontend's MusicTrackTile.
  useEffect(() => {
    const waveEl = waveContainerRef.current;
    if (!waveEl) return;
    // Hidden <audio> element so the browser handles streaming + seeking.
    const audio = new Audio(track.url);
    audio.crossOrigin = 'anonymous';
    audio.preload = 'auto';
    audioRef.current = audio;
    const ws = WaveSurfer.create({
      container: waveEl,
      url: track.url,
      waveColor: 'rgba(255, 255, 255, 0.25)',
      progressColor: 'rgba(244, 114, 182, 0.95)', // pink-400
      cursorColor: 'transparent',
      height: 24,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      normalize: true,
      interact: true,
      autoplay: false,
    });
    waveSurferRef.current = ws;
    ws.on('ready', (d) => {
      setIsLoading(false);
      // Honour the click — user already asked to play.
      if (audioRef.current?.paused === false) {
        ws.play().catch(() => setIsPlaying(false));
        setIsPlaying(true);
      }
    });
    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));
    ws.on('finish', () => { setIsPlaying(false); setProgress(0); currentTimeRef.current = 0; });
    ws.on('timeupdate', (t) => { setProgress(t); currentTimeRef.current = t; });
    return () => {
      try { ws.destroy(); } catch { /* noop */ }
      waveSurferRef.current = null;
      audioRef.current = null;
    };
  }, [track.id, track.url]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const ws = waveSurferRef.current;
    if (!ws) return;
    if (isPlaying) {
      ws.pause();
      setIsPlaying(false);
      audioBus.stop(track.id);
    } else {
      // Claim the bus slot first — if another tile is playing, the bus
      // fires a stop event for it so we don't end up with overlapping
      // audio.
      audioBus.play(track.id);
      setIsLoading(true);
      ws.play()
        .then(() => { setIsLoading(false); setIsPlaying(true); })
        .catch(() => { setIsLoading(false); setIsPlaying(false); audioBus.stop(track.id); });
    }
  };

  // If another tile starts to play, the bus fires `audiobus:stop`
  // with `detail: { id: <previous owner> }`. If that's us, pause our
  // wavesurfer so only one track plays at a time.
  useEffect(() => {
    return audioBus.onStopRequest((id) => {
      if (id !== track.id) return; // not us — ignore
      const ws = waveSurferRef.current;
      if (ws) {
        try { ws.pause(); } catch { /* noop */ }
      }
      setIsPlaying(false);
    });
  }, [track.id]);

  // On unmount, release the bus slot so the next card can claim it.
  useEffect(() => {
    return () => {
      audioBus.stop(track.id);
    };
  }, [track.id]);

  // Stop the preview before invoking Edit / Delete so no audio leaks
  // into the modal (and so the user can hear the new preview cleanly).
  const handleEdit = () => {
    const ws = waveSurferRef.current;
    if (ws) {
      try { ws.pause(); } catch { /* noop */ }
    }
    setIsPlaying(false);
    audioBus.stop(track.id);
    onEdit();
  };
  const handleDelete = () => {
    const ws = waveSurferRef.current;
    if (ws) {
      try { ws.pause(); } catch { /* noop */ }
    }
    setIsPlaying(false);
    audioBus.stop(track.id);
    onDelete();
  };

  const formatDur = (sec: number | null) => {
    if (!sec) return "—";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const langFlag = LANGUAGES.find((l) => l.value === track.language)?.flag ?? "🌐";
  const proposalTypeBadge = (track as any).proposalType
    ? PROPOSAL_TYPE_META_ADMIN[(track as any).proposalType as string]
    : null;

  // Deterministic colour-by-id gradient (matches the web card).
  const paletteHash = track.id
    .split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const coverGradient = TRACK_PALETTES[paletteHash % TRACK_PALETTES.length];

  return (
    <div className={cn(
      "group relative block w-full overflow-hidden rounded-2xl border text-left transition-all duration-300 cursor-pointer",
      "border-white/10 bg-white/[0.04] hover:border-white/25 hover:bg-white/[0.06]",
      isPlaying && "border-pink-400/50 shadow-[0_8px_30px_-8px_rgba(236,72,153,0.4)]",
    )}>
      <audio ref={audioRef} src={track.url} preload="none" />

      {/* Hero — matches the web card (h-32 cover with overlay + badges) */}
      <div className="relative h-32 overflow-hidden">
        {track.coverArtUrl ? (
          <img
            src={track.coverArtUrl}
            alt=""
            className={cn(
              "h-full w-full object-cover transition-transform duration-500",
              isPlaying ? "scale-110" : "group-hover:scale-105",
            )}
            loading="lazy"
          />
        ) : (
          <div className={cn("h-full w-full bg-gradient-to-br", coverGradient)} />
        )}

        {/* Overlay gradient for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

        {/* Top-left badges — PRO + All (mirrors web card) */}
        <div className="absolute left-2 top-2 flex items-center gap-1">
          {track.tier === "PRO" && (
            <span className="flex items-center gap-0.5 rounded-full bg-blue-900 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
              ★
              PRO
            </span>
          )}
          {track.isGeneric && (
            <span className="rounded-full bg-white/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white backdrop-blur">
              All
            </span>
          )}
        </div>

        {/* Top-right proposal-type badge — new for admin */}
        {proposalTypeBadge && (
          <div className="absolute right-2 top-2">
            <span
              title={`Composed for ${proposalTypeBadge.label.toLowerCase()} proposals`}
              className={cn(
                "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white backdrop-blur",
                proposalTypeBadge.classes,
              )}
            >
              <span aria-hidden>{proposalTypeBadge.emoji}</span>
              {proposalTypeBadge.label}
            </span>
          </div>
        )}

        {/* Title + artist + duration — bottom overlay like the web card */}
        <div className="absolute inset-x-0 bottom-0 p-3">
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-bold text-white drop-shadow">{track.title}</h3>
              <p className="truncate text-[11px] text-white/75">{track.artist || "—"}</p>
            </div>
            <span className="shrink-0 rounded-full bg-black/40 px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-white/80 backdrop-blur">
              {formatDur(track.duration)}
            </span>
          </div>
        </div>
      </div>

      {/* Body — bottom row: play button + tag chips (mirrors web card) */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        {/* Play / pause / loading — same logic + visual as the web card */}
        <button
          type="button"
          onClick={togglePlay}
          aria-label={isLoading ? "Loading preview" : isPlaying ? "Pause preview" : "Play preview"}
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-transform active:scale-95",
            isLoading
              ? "bg-white/15 text-zinc-900 cursor-wait"
              : isPlaying
                ? "bg-merry-pink-gradient text-white shadow-[0_0_18px_rgba(255,51,119,0.55)]"
                : "bg-white text-zinc-900 hover:scale-105",
          )}
        >
          {isLoading
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : isPlaying
              ? <Pause className="h-4 w-4" fill="currentColor" />
              : <Play  className="ml-0.5 h-4 w-4" fill="currentColor" />}
        </button>

        {/* Tag chips — same flex-wrap style as the web card */}
        <div className="flex flex-1 items-center justify-end gap-1 overflow-hidden">
          {track.genre && (
            <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] capitalize text-white/70">
              {track.genre}
            </span>
          )}
          {track.mood && (
            <span className="rounded-full bg-pink-500/15 px-1.5 py-0.5 text-[10px] text-pink-300">
              {track.mood}
            </span>
          )}
          <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] text-white/70" title={track.language}>
            {langFlag} {track.language.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Wavesurfer-based waveform + progress — matches the web frontend.
          Slides in when playing; the same instance drives the visual
          bars AND doubles as a seek/seek-to-time control via the
          built-in click handler. */}
      <div className={cn(
        "overflow-hidden border-t border-white/5 transition-all duration-300",
        isPlaying ? "max-h-8 opacity-100" : "max-h-0 opacity-0",
      )}>
        <div className="flex items-center gap-2 px-3 py-1.5">
          <MiniEqualizer active={isPlaying} />
          <span className="font-mono text-[10px] tabular-nums text-white/50 w-8">
            {formatTime(currentTimeRef.current)}
          </span>
          <div ref={waveContainerRef} className="flex-1 h-6" />
          <span className="font-mono text-[10px] tabular-nums text-white/50 w-8 text-right">
            {formatDur(track.duration)}
          </span>
        </div>
      </div>

      {/* Admin actions — Edit / Delete (not present on the web card).
          Both buttons first stop the preview + release the audio bus
          slot, so no audio leaks into the modal. */}
      <div className="flex items-center justify-end gap-1 border-t border-white/5 px-3 py-2">
        <button
          type="button"
          onClick={handleEdit}
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-semibold text-white/70 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
        >
          <Pencil className="h-3 w-3" />
          Edit
        </button>
        <button
          type="button"
          onClick={handleDelete}
          className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/20 bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-300 transition hover:border-red-500/40 hover:bg-red-500/20"
        >
          <Trash2 className="h-3 w-3" />
          Delete
        </button>
      </div>
    </div>
  );
}

/* ─── Mini equalizer ─────────────────────────────────────────────────── */

function MiniEqualizer({ active }: { active: boolean }) {
  // 5 bars, animated heights when active
  const [bars, setBars] = useState([0.3, 0.5, 0.7, 0.4, 0.6]);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      setBars((prev) => prev.map(() => 0.25 + Math.random() * 0.75));
    }, 110);
    return () => clearInterval(id);
  }, [active]);

  return (
    <div className="flex h-5 w-7 items-end justify-between gap-[2px]">
      {bars.map((h, i) => (
        <span
          key={i}
          className="block w-[3px] rounded-sm bg-merry-pink-gradient transition-[height] duration-100"
          style={{ height: `${active ? h * 100 : 18}%`, opacity: active ? 1 : 0.4 }}
        />
      ))}
    </div>
  );
}

/* ─── Tags tab — manage Music Themes & Moods ─────────────────────────── */

function TagsTab() {
  const [tags, setTags] = useState<MusicTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<MusicTag | null>(null);
  const [creatingKind, setCreatingKind] = useState<"theme" | "mood" | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get<{ success: boolean; tags: MusicTag[] }>(
        "/admin/music-tags?includeInactive=true",
      );
      if (res.success) setTags(res.tags);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const themes = tags.filter((t) => t.kind === "theme");
  const moods = tags.filter((t) => t.kind === "mood");

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-2">
        <TagColumn
          title="Music Themes"
          description="Themes shown on the create-music wizard (e.g. 'Our Story')."
          icon={Sparkles}
          tags={themes}
          loading={loading}
          onCreate={() => setCreatingKind("theme")}
          onEdit={setEditing}
          onChange={load}
        />
        <TagColumn
          title="Music Moods"
          description="Moods shown on the create-music wizard (e.g. 'Romantic')."
          icon={Headphones}
          tags={moods}
          loading={loading}
          onCreate={() => setCreatingKind("mood")}
          onEdit={setEditing}
          onChange={load}
        />
      </div>

      <TagEditorDialog
        open={!!editing}
        tag={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
      <TagEditorDialog
        open={!!creatingKind}
        kind={creatingKind ?? undefined}
        onClose={() => setCreatingKind(null)}
        onSaved={() => { setCreatingKind(null); load(); }}
      />
    </>
  );
}

function TagColumn({
  title, description, icon: Icon, tags, loading, onCreate, onEdit, onChange,
}: {
  title: string;
  description: string;
  icon: typeof Music;
  tags: MusicTag[];
  loading: boolean;
  onCreate: () => void;
  onEdit: (t: MusicTag) => void;
  onChange: () => void;
}) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    setConfirmDeleteId(null);
    try {
      await adminApi.delete(`/admin/music-tags/${id}`);
      onChange();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-merry-pink-gradient text-white shadow-[0_4px_18px_-6px_rgba(255,51,119,0.55)]">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-base font-bold text-white">{title}</h2>
            <p className="mt-0.5 text-xs text-white/55">{description}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex items-center gap-1.5 rounded-xl bg-merry-pink-gradient px-3 py-1.5 text-xs font-semibold text-white shadow-[0_6px_18px_-6px_rgba(255,51,119,0.55)] transition hover:scale-[1.02] active:scale-[0.99]"
        >
          <Plus className="h-3.5 w-3.5" />
          New
        </button>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-pink-400" />
          </div>
        ) : tags.length === 0 ? (
          <div className="py-10 text-center text-xs text-white/40">
            None yet — click <span className="font-semibold text-white/60">New</span> to add one.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {tags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => onEdit(tag)}
                className={cn(
                  "group relative flex flex-col items-center gap-1.5 rounded-xl border px-3 py-3 text-center transition",
                  tag.isActive
                    ? "border-white/10 bg-white/[0.04] hover:border-pink-400/40 hover:bg-pink-500/10"
                    : "border-white/5 bg-white/[0.02] opacity-50 hover:opacity-80",
                )}
              >
                <span className="text-2xl leading-none">{tag.icon || tag.name.charAt(0).toUpperCase()}</span>
                <span className="truncate text-xs font-semibold text-white">{tag.name}</span>
                <span className="truncate text-[10px] text-white/40">/{tag.slug}</span>
                {!tag.isActive && (
                  <span className="absolute right-1.5 top-1.5 rounded-full bg-white/10 px-1 py-px text-[8px] uppercase tracking-wider text-white/60">
                    Hidden
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(tag.id); }}
                  className="absolute right-1.5 bottom-1.5 rounded p-1 text-white/30 opacity-0 transition hover:bg-red-500/20 hover:text-red-300 group-hover:opacity-100"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </button>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => confirmDeleteId && handleDelete(confirmDeleteId)}
        title="Delete this tag?"
        description="This permanently removes the tag. Existing tracks won't be affected."
        confirmLabel="Delete tag"
        variant="destructive"
      />
    </div>
  );
}

function TagEditorDialog({
  open, tag, kind, onClose, onSaved,
}: {
  open: boolean;
  tag?: MusicTag | null;
  kind?: "theme" | "mood";
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(tag?.name ?? "");
  const [slug, setSlug] = useState(tag?.slug ?? "");
  const [icon, setIcon] = useState(tag?.icon ?? "");
  const [description, setDescription] = useState(tag?.description ?? "");
  const [displayOrder, setDisplayOrder] = useState<number>(tag?.displayOrder ?? 0);
  const [isActive, setIsActive] = useState<boolean>(tag?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(tag?.name ?? "");
      setSlug(tag?.slug ?? "");
      setIcon(tag?.icon ?? "");
      setDescription(tag?.description ?? "");
      setDisplayOrder(tag?.displayOrder ?? 0);
      setIsActive(tag?.isActive ?? true);
      setError(null);
    }
  }, [open, tag]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        slug: slug.trim() || undefined,
        icon: icon.trim() || undefined,
        description: description.trim() || undefined,
        displayOrder,
        isActive,
        ...(tag ? {} : { kind }),
      };
      if (tag) {
        await adminApi.patch(`/admin/music-tags/${tag.id}`, payload);
      } else {
        await adminApi.post("/admin/music-tags", payload);
      }
      onSaved();
    } catch (e: any) {
      setError(e.message || "Failed to save tag");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={tag ? `Edit ${tag.kind === "theme" ? "Theme" : "Mood"}` : `New ${kind === "theme" ? "Theme" : "Mood"}`}
      width={520}
    >
      {error && (
        <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}
      <div className="space-y-4">
        <div className="grid grid-cols-[1fr_72px] gap-3">
          <Field label="Name *" htmlFor="tag-name">
            <input
              id="tag-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kind === "theme" ? "Our Story" : "Romantic"}
              className={inputCls}
            />
          </Field>
          <Field label="Icon" htmlFor="tag-icon">
            <input
              id="tag-icon"
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="💕"
              maxLength={4}
              className={cn(inputCls, "text-center text-xl")}
            />
          </Field>
        </div>
        <Field label="Slug (auto if blank)" htmlFor="tag-slug">
          <input
            id="tag-slug"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder={kind === "theme" ? "our-story" : "romantic"}
            className={cn(inputCls, "font-mono text-xs")}
          />
        </Field>
        <Field label="Description" htmlFor="tag-desc">
          <input
            id="tag-desc"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional one-liner shown in tooltips."
            className={inputCls}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Display order" htmlFor="tag-order">
            <input
              id="tag-order"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
              className={inputCls}
            />
          </Field>
          <Field label="Visibility" htmlFor="tag-active">
            <button
              id="tag-active"
              type="button"
              onClick={() => setIsActive((v) => !v)}
              className={cn(
                "inline-flex h-[42px] items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition",
                isActive
                  ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
                  : "border-white/10 bg-white/[0.04] text-white/60",
              )}
            >
              {isActive ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
              {isActive ? "Active" : "Hidden"}
            </button>
          </Field>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3 border-t border-white/10 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-xl bg-merry-pink-gradient px-5 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-6px_rgba(255,51,119,0.55)] transition hover:scale-[1.02] active:scale-[0.99] disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {tag ? "Save changes" : "Create tag"}
        </button>
      </div>
    </Dialog>
  );
}

/* ─── Shared UI atoms ────────────────────────────────────────────────── */

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white placeholder-white/30 outline-none transition focus:border-pink-500/50 focus:bg-white/[0.07]";

function Section({ title, children, cols = 1 }: { title: string; children: React.ReactNode; cols?: 1 | 2 }) {
  return (
    <div>
      <h3 className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white/40">
        {title}
      </h3>
      <div className={cn("space-y-3", cols === 2 && "grid grid-cols-2 gap-3 space-y-0")}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.14em] text-white/55">
        {label}
      </span>
      {children}
    </label>
  );
}

function ToggleRow({ label, description, checked, onChange }: {
  label: string; description?: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={cn(
        "flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition",
        checked ? "border-pink-500/30 bg-pink-500/[0.06]" : "border-white/10 bg-white/[0.03]",
      )}
    >
      <div>
        <p className="text-sm font-semibold text-white">{label}</p>
        {description && <p className="mt-0.5 text-xs text-white/50">{description}</p>}
      </div>
      <span className={cn(
        "relative h-5 w-9 shrink-0 rounded-full transition",
        checked ? "bg-merry-pink-gradient" : "bg-white/15",
      )}>
        <span className={cn(
          "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition",
          checked ? "left-[18px]" : "left-0.5",
        )} />
      </span>
    </button>
  );
}

function SelectChips({ value, onChange, options }: {
  value: string; onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="-mx-1 flex flex-wrap gap-1 px-1">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "rounded-full px-2.5 py-1 text-xs font-medium transition",
              active
                ? "bg-merry-pink-gradient text-white shadow-[0_0_14px_-4px_rgba(255,51,119,0.55)]"
                : "bg-white/5 text-white/70 ring-1 ring-white/10 hover:bg-white/10 hover:text-white",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ChipGroup({ label, value, options, onChange }: {
  label: string; value: string; onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="hidden text-[10px] font-bold uppercase tracking-[0.16em] text-white/40 lg:inline">
        {label}
      </span>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                "rounded-full px-2.5 py-1 text-xs font-medium transition",
                active
                  ? "bg-merry-pink-gradient text-white shadow-[0_0_14px_-4px_rgba(255,51,119,0.55)]"
                  : "bg-white/5 text-white/65 ring-1 ring-white/10 hover:bg-white/10 hover:text-white",
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ChipDropdown({ icon: Icon, label, value, options, onChange }: {
  icon: typeof Globe; label: string; value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const current = options.find((o) => o.value === value);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/80 transition hover:bg-white/[0.07]"
      >
        <Icon className="h-3.5 w-3.5 text-white/50" />
        <span>{current?.label ?? label}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 text-white/40 transition", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 min-w-[180px] overflow-hidden rounded-xl border border-white/10 bg-zinc-900/95 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.8)] backdrop-blur-xl">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={cn(
                "block w-full px-3 py-2 text-left text-sm transition",
                opt.value === value
                  ? "bg-pink-500/20 text-white"
                  : "text-white/80 hover:bg-white/5",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatsRow({ stats }: { stats: Stats }) {
  const cards = [
    { label: "Total tracks", value: stats.total, icon: Music, accent: "from-pink-500/20 to-rose-500/10" },
    { label: "Active",       value: stats.active, icon: Volume2, accent: "from-emerald-500/20 to-teal-500/10" },
    { label: "Featured",     value: stats.featured, icon: Star, accent: "from-amber-500/20 to-orange-500/10" },
    { label: "Moods covered", value: stats.byMood.length, icon: Filter, accent: "from-violet-500/20 to-fuchsia-500/10" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cards.map((c) => (
        <div key={c.label} className={`relative overflow-hidden rounded-2xl border border-white/5 bg-gradient-to-br ${c.accent} p-4`}>
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 backdrop-blur">
              <c.icon className="h-4 w-4 text-white" />
            </span>
            <div>
              <p className="text-2xl font-bold tabular-nums text-white">{c.value}</p>
              <p className="text-xs text-white/55">{c.label}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
